import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import { CacheProvider } from './contexts/CacheContext';
import { ConfirmationProvider } from './contexts/ConfirmationContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';
import Overview from './pages/Overview';
import VenueInfoNew from './pages/VenueInfoNew';
import VenueCourtsNew from './pages/VenueCourtsNew';
import BookingsManagementNew from './pages/BookingsManagementNew';
import RevenueNew from './pages/RevenueNew';
import PayoutsNew from './pages/PayoutsNew';
import DebtsAndRefunds from './pages/DebtsAndRefunds';
import TransactionsNew from './pages/TransactionsNew';
import MembershipsNew from './pages/MembershipsNew';
import CustomerInsightsNew from './pages/CustomerInsightsNew';
import PromotionsNew from './pages/PromotionsNew';
import ReviewsNew from './pages/ReviewsNew';
import Support from './pages/Support';
import SettingsNew from './pages/SettingsNew';
import TeamNew from './pages/TeamNew';
import ActivityNew from './pages/ActivityNew';


// Configure React Query Client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Cache time (formerly cacheTime) - keep in memory for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when user returns to tab (can be annoying)
      refetchOnReconnect: true, // Refetch when network reconnects
      retry: 1, // Retry failed requests once
      refetchOnMount: false, // Don't refetch on component mount if data is fresh
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CacheProvider>
          <ConfirmationProvider>
          <Router>
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/overview" replace />} />
                  <Route path="/overview" element={<Overview />} />
                  <Route path="/venue-info" element={<VenueInfoNew />} />
                  <Route path="/courts" element={<VenueCourtsNew />} />
                  <Route path="/bookings" element={<BookingsManagementNew />} />
                  <Route path="/revenue" element={<RevenueNew />} />
                  <Route path="/payouts" element={<PayoutsNew />} />
                  <Route path="/debts" element={<DebtsAndRefunds />} />
                  <Route path="/transactions" element={<TransactionsNew />} />
                  <Route path="/memberships" element={<MembershipsNew />} />
                  <Route path="/customer-insights" element={<CustomerInsightsNew />} />
                  <Route path="/promotions" element={<PromotionsNew />} />
                  <Route path="/reviews" element={<ReviewsNew />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/team" element={<TeamNew />} />
                  <Route path="/activity" element={<ActivityNew />} />
                  <Route path="/settings" element={<SettingsNew />} />
                  <Route path="*" element={<Navigate to="/overview" replace />} />
                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          </Router>
          </ConfirmationProvider>
        </CacheProvider>
      </AuthProvider>
      {/* React Query DevTools - only shows in development */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
